import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import {
  HomeModernIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  EyeIcon,
  BanknotesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface Rental {
  id: string;
  assetId: number;
  tenantName: string;
  tenantEmail?: string | null;
  tenantPhone?: string | null;
  monthlyRent: number;
  paymentDay?: number | null;
  startDate?: string;
  endDate?: string | null;
  status: 'active' | 'terminated';
}

const Rentals: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('active');
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; rentalId?: string; tenantName?: string }>({ isOpen: false });

  const { data: rentals, isLoading, error, refetch } = useQuery<Rental[]>(
    'rentals',
    async () => {
      const response = await api.get('/rentals');
      return response.data;
    },
    { staleTime: 1000 * 60 * 5, refetchOnMount: 'always', refetchOnWindowFocus: true, refetchOnReconnect: true }
  );

  const deleteRental = async (id: string) => {
    await api.delete(`/rentals/${id}`);
    refetch();
  };

  const filtered =
    rentals?.filter(r => {
      const assetName = (r as any).assetDescription || '';
      const matchesSearch = (
        ((r.tenantName || '') + ' ' + assetName).toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = selectedStatus === 'all' || r.status === selectedStatus;
      return matchesSearch && matchesStatus;
    }) || [];

  const activeRentals = rentals?.filter(r => r.status === 'active') || [];
  const terminatedRentals = rentals?.filter(r => r.status === 'terminated') || [];
  const totalMonthlyRent = activeRentals.reduce((sum, r) => sum + (r.monthlyRent || 0), 0);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatDateOnly = (s?: string) => {
    if (!s) return '-';
    const parts = String(s).split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : s;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-500">Erro ao carregar aluguéis.</div>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <HomeModernIcon className="h-8 w-8 mr-3 text-primary-600" />
            Gestão de Aluguéis
          </h1>
          <p className="text-gray-600 mt-2">Contratos de locação de imóveis</p>
        </div>
        
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[120px] flex items-center">
          <div className="flex items-center w-full">
            <div className="flex-shrink-0">
              <HomeModernIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4 w-full">
              <p className="text-sm font-medium text-gray-600 mb-3">Resumo</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-lg font-bold text-gray-900">{rentals?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Ativos</p>
                  <p className="text-lg font-bold text-gray-900">{activeRentals.length}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Encerrados</p>
                  <p className="text-lg font-bold text-gray-900">{terminatedRentals.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[120px] flex items-center">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4 min-w-0">
              <p className="text-sm font-medium text-gray-600">Aluguel Mensal Total (ativos)</p>
              <p className="text-2xl font-bold text-gray-900 break-words whitespace-normal leading-tight">
                {formatCurrency(totalMonthlyRent)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por inquilino..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]"
            >
              <option value="all">Todos os Status</option>
              <option value="active">Ativo</option>
              <option value="terminated">Encerrado</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filtered.map(r => (
          <div key={r.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <HomeModernIcon className="h-6 w-6 text-primary-600" />
                <button onClick={() => navigate(`/assets/${r.assetId}`)} className="ml-3 text-lg font-semibold text-gray-900 hover:underline">
                  {(r as any).assetDescription || `#${r.assetId}`}
                </button>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(r.status)}`}>
                {formatStatus(r.status)}
              </span>
            </div>
            <div className="mt-4 space-y-2 text-sm text-gray-700">
              <div className="flex justify-between"><span className="text-gray-500">Inquilino</span><span className="text-gray-900">{r.tenantName}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Aluguel Mensal</span><span className="text-gray-900">{formatCurrency(r.monthlyRent)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Dia de Pagamento</span><span className="text-gray-900">{r.paymentDay ?? '-'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Início</span><span className="text-gray-900">{formatDateOnly(r.startDate)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Término</span><span className="text-gray-900">{formatDateOnly(r.endDate)}</span></div>
            </div>
            <div className="mt-6 flex items-center justify-end space-x-2">
              <button
                onClick={() => navigate(`/assets/${r.assetId}`)}
                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                title="Ver Imóvel"
              >
                <EyeIcon className="h-4 w-4 mr-1" />
                Ver
              </button>
              {hasPermission('assets.update') && (
                <button
                  onClick={() => navigate(`/rentals/${r.id}/edit`)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-primary-200 text-sm font-medium rounded-xl text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                  title="Editar Aluguel"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Editar
                </button>
              )}
              {hasPermission('assets.delete') && (
                <button
                  onClick={() => setDeleteModal({ isOpen: true, rentalId: r.id, tenantName: r.tenantName })}
                  className="px-3 py-2 border border-red-200 text-sm font-medium rounded-xl text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                  title="Remover Aluguel"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-gray-500 py-8">Nenhum aluguel encontrado.</div>
        )}
      </div>
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false })}
        onConfirm={async () => {
          if (deleteModal.rentalId) {
            await deleteRental(deleteModal.rentalId);
            setDeleteModal({ isOpen: false });
          }
        }}
        title="Remover Aluguel"
        message={`Tem certeza que deseja remover o aluguel de ${deleteModal.tenantName || ''}? Esta ação não pode ser desfeita.`}
        confirmText="Remover"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default Rentals;
